/**
 * "Add to Home Screen" install banner.
 *
 * main.js mounts this at boot when:
 *   - not already running in standalone / PWA mode
 *   - STORAGE_KEYS.SEEN_INSTALL_BANNER is NOT set in storage
 *
 * Platform behavior:
 *   - Android (and any Chromium browser that fires
 *     beforeinstallprompt): shows a native "Install" action button
 *     that calls prompt() on the deferred event. main.js passes the
 *     event in via opts.installPromptEvent.
 *   - iOS Safari: shows the manual "Share → Add to Home Screen"
 *     instruction, because iOS does not fire beforeinstallprompt.
 *   - Anything else: falls back to the generic "Add to Home Screen"
 *     instruction.
 *
 * onDismiss persists SEEN_INSTALL_BANNER so the banner is never shown
 * again on this device.
 *
 * Signature:
 *   mountInstallBanner(onDismiss, opts?)
 *     opts.installPromptEvent  BeforeInstallPromptEvent | null
 *     opts.platform            'ios' | 'android' | 'other' (optional
 *                              override for tests)
 */

import { isIOS, isAndroid } from '../platform.js';

const BANNER_ID = 'install-banner';

function detectPlatform() {
  if (isIOS()) return 'ios';
  if (isAndroid()) return 'android';
  return 'other';
}

function messageFor(platform, hasNativePrompt) {
  if (hasNativePrompt) return 'Install this app for quick access';
  if (platform === 'ios') return 'Tap Share \u2192 Add to Home Screen to install';
  if (platform === 'android') return 'Open the browser menu and tap "Add to Home Screen"';
  return 'Add to Home Screen to install';
}

/**
 * @param {() => void} onDismiss
 * @param {{ installPromptEvent?: any, platform?: string }} [opts]
 */
export function mountInstallBanner(onDismiss, opts = {}) {
  if (document.getElementById(BANNER_ID)) return;

  const platform = opts.platform || detectPlatform();
  const promptEvent = opts.installPromptEvent || null;
  const hasNativePrompt = promptEvent && typeof promptEvent.prompt === 'function';

  const banner = document.createElement('div');
  banner.id = BANNER_ID;
  banner.className = 'install-banner';
  banner.setAttribute('role', 'status');

  const actionLabel = hasNativePrompt ? 'Install' : 'Got it';

  banner.innerHTML = `
    <span class="install-banner__text">
      ${messageFor(platform, hasNativePrompt)}
    </span>
    <button type="button" class="install-banner__action" data-action="primary">
      ${actionLabel}
    </button>
    <button type="button" class="install-banner__close" data-action="close"
            aria-label="Close">
      <span aria-hidden="true">\u00D7</span>
    </button>
  `;

  const dismiss = () => {
    if (banner.parentNode) banner.parentNode.removeChild(banner);
    try { onDismiss(); } catch (_e) { /* caller bug */ }
  };

  banner.querySelector('[data-action="primary"]').addEventListener('click', async () => {
    if (hasNativePrompt) {
      try {
        promptEvent.prompt();
        await promptEvent.userChoice;
      } catch (_e) { /* user dismissed / already consumed */ }
    }
    dismiss();
  });
  banner.querySelector('[data-action="close"]').addEventListener('click', dismiss);

  document.body.appendChild(banner);
}
